import React, { useEffect, useRef, useState } from "react";
import FontIcon from "react-md/lib/FontIcons";
import Tooltip from "@material-ui/core/Tooltip";
import callSession, { sessionAppointment, sessionCall } from "./callSession";
import callEventStream from "service/realtime/CallEventStream";
import EventService from "service/event/EventService";
import LTRService from "../service/LTRService";
import "../styles/ltrCall.scss";

const ltrService = new LTRService();

// After hang-up the pill stays as a receipt for this long, then dismisses
// itself — unless the pointer is over it.
const RECEIPT_MS = 8000;

// SparkTG's after-call record reaches the case a second or two after hang-up;
// the case is re-read this long after the end so the receipt can show it.
const RESULT_REFRESH_MS = 4000;

// While the call is live the case is re-read this often: if SparkTG's record
// of the call has reached it, the call is over even if the stream missed it.
const CASE_POLL_MS = 30000;

// "Tap when answered" waits for the patient's phone to ring, so the doctor
// cannot mark a call connected while their own phone is still ringing. These
// stop it waiting forever when the stream is not delivering: no event at all
// this long after dialling, or the doctor's phone "ringing" for longer than
// SparkTG ever rings it (the patient's ring was missed).
const NO_EVENT_FALLBACK_MS = 10000;
const CALLER_RING_FALLBACK_MS = 60000;

const call = (fn, ...args) =>
  new Promise((resolve, reject) => fn(...args, resolve, reject));

function clock(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

const STEPS = [
  { kind: "appointment", icon: "event", label: "Create appointment" },
  { kind: "rx", icon: "description", label: "Create Rx" },
  { kind: "action", icon: "flag", label: "Assign action" },
];

/**
 * The in-call pill: from the moment a call is placed until a few seconds after
 * hang-up. It walks the doctor through the call's steps — create the
 * appointment, then the Rx, then the action with the follow-up date — one
 * labelled step at a time, and ends the call on the server when they hang up.
 */
function InCallPill({ history }) {
  const [session, setSession] = useState(callSession.get());
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  // The live stream gave up for this user: the tap is offered straight away.
  const [streamDown, setStreamDown] = useState(false);
  // The red button was pressed while SparkTG still had the call on.
  const [confirmEnd, setConfirmEnd] = useState(false);
  const hoverRef = useRef(false);
  const endingRef = useRef(false);

  useEffect(() => callSession.subscribe(setSession), []);

  const live = !!session && session.phase !== "ended";
  const liveCallId = live ? session.callId : null;

  // Tick the timer while a call is live.
  useEffect(() => {
    if (!live) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [live]);

  // Follow this call on the SparkTG stream: whose phone is ringing, and the
  // hang-up. It cannot tell ringing from talking, so "connected" stays the
  // doctor's tap. Only events tagged with this LTR call count.
  useEffect(() => {
    if (!live) return undefined;
    setStreamDown(false);
    setConfirmEnd(false);
    const unsubscribe = callEventStream.subscribe((event) => {
      const current = callSession.get();
      if (!current || current.phase === "ended") return;
      if (event.type === "unavailable") {
        setStreamDown(true);
        return;
      }
      const liveCall = event.call;
      const ours = !!liveCall && Number(liveCall.ltrCallId) === Number(current.callId);
      if (event.type === "snapshot") {
        // Sent on every (re)connect. Our call still on: carry on. Gone, after
        // we had seen it: it probably ended while the stream was away -- but
        // a restarted wrapper also forgets live calls, so check the case for
        // SparkTG's record instead of hanging up on a call still in progress.
        if (ours && liveCall.state !== "ended") noteRinging(current, liveCall);
        else if (current.xnid) {
          // The stream no longer vouches for the call, so the red button
          // goes back to ending it straight away.
          callSession.update({ xnid: null });
          setTimeout(refreshFromServer, RESULT_REFRESH_MS);
        }
        return;
      }
      if (event.type !== "call" || !ours) return;
      if (liveCall.state === "ended") hangUp({ fromStream: true });
      else noteRinging(current, liveCall);
    });
    callEventStream.start();
    if (callEventStream.unavailable) setStreamDown(true);
    return () => {
      unsubscribe();
      callEventStream.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, liveCallId]);

  // Safety net for a hang-up the stream never delivered.
  useEffect(() => {
    if (!live) return undefined;
    const t = setInterval(refreshFromServer, CASE_POLL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, liveCallId]);

  // Dismiss the receipt on its own.
  const endedCallId = session && session.phase === "ended" ? session.callId : null;

  // Pick up SparkTG's record of the call for the receipt and the call audit.
  useEffect(() => {
    if (endedCallId == null) return undefined;
    const t = setTimeout(refreshFromServer, RESULT_REFRESH_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endedCallId]);

  useEffect(() => {
    if (endedCallId == null) return undefined;
    let t = setTimeout(function dismiss() {
      if (hoverRef.current) {
        t = setTimeout(dismiss, 1500);
        return;
      }
      const current = callSession.get();
      if (current && current.callId === endedCallId) callSession.clear();
    }, RECEIPT_MS);
    return () => clearTimeout(t);
  }, [endedCallId]);

  if (!session) return null;

  const connected = session.phase === "connected";
  const ended = session.phase === "ended";
  const appt = sessionAppointment(session);
  const done = {
    appointment: !!appt,
    rx: !!(appt && appt.prescriptionId),
    action: !!(appt && appt.actions && appt.actions.length),
  };
  // The step on offer now: each one waits for the step before it.
  const available = (kind) => {
    if (!connected) return false;
    if (kind === "appointment") return !done.appointment;
    if (kind === "rx") return done.appointment && !done.rx;
    return done.rx && !done[kind];
  };

  const elapsed = connected && session.connectedAt
    ? (now - new Date(session.connectedAt).getTime()) / 1000
    : 0;

  const sinceDial = now - (session.dialledAt || Date.parse(session.startedAt) || now);
  const ringingFor = session.ringingSince ? now - session.ringingSince : 0;
  const canTap =
    streamDown ||
    session.ringing === "patient" ||
    (!session.ringing && sinceDial > NO_EVENT_FALLBACK_MS) ||
    (session.ringing === "caller" && ringingFor > CALLER_RING_FALLBACK_MS);
  let ringingLabel = "Ringing…";
  if (session.ringing === "patient") ringingLabel = "Ringing patient… · tap when answered";
  else if (canTap) ringingLabel = "Ringing… · tap when answered";
  else if (session.ringing === "caller") ringingLabel = "Ringing your phone…";
  let ringingTip = "Connecting the call…";
  if (canTap) ringingTip = "Tap when the patient answers";
  else if (session.ringing === "caller") ringingTip = "Pick up your phone — the patient is rung once you answer";

  // The stream has seen this call and no hang-up yet. The red button cannot
  // hang up the phone (SparkTG has no API for it), so while the call is on it
  // asks the doctor to hang up there instead of ending the call on the case:
  // ended early, the call would read as missed while the conversation goes on.
  const callOnPerStream = !streamDown && !!session.xnid;
  const pressHangUp = () => {
    if (callOnPerStream) setConfirmEnd(true);
    else hangUp();
  };

  const markConnected = () => {
    if (session.phase !== "dialling" || busy || !canTap) return;
    const connectedAt = new Date().toISOString();
    callSession.update({ phase: "connected", connectedAt });
    call(ltrService.updateCall, session.caseId, session.callId, { connected: true })
      .then((updated) => callSession.syncCase(updated))
      .catch((err) => console.error("Error marking LTR call connected:", err));
  };

  // Records what the stream says about the live call.
  function noteRinging(current, liveCall) {
    const patch = {};
    if (liveCall.callId && liveCall.callId !== current.xnid) patch.xnid = liveCall.callId;
    if (liveCall.ringing && liveCall.ringing !== current.ringing) {
      patch.ringing = liveCall.ringing;
      patch.ringingSince = Date.now();
    }
    if (Object.keys(patch).length) callSession.update(patch);
  }

  // Re-reads the case. If SparkTG's record of the call has reached it, the
  // call is over: end the pill without another round trip.
  function refreshFromServer() {
    const current = callSession.get();
    if (!current) return;
    call(ltrService.fetchCase, current.caseId)
      .then((updated) => {
        callSession.syncCase(updated);
        const latest = callSession.get();
        if (!latest || latest.callId !== current.callId || latest.phase === "ended") return;
        const record = sessionCall(latest);
        if (record && record.telephony && record.telephony.disposition) {
          callSession.update({
            phase: "ended",
            endedAt: record.endedAt || new Date().toISOString(),
            durationSeconds: record.durationSeconds || 0,
          });
        }
      })
      .catch((err) => console.error("Error refreshing the LTR case:", err));
  }

  function hangUp({ fromStream } = {}) {
    const current = callSession.get();
    if (!current || current.phase === "ended" || endingRef.current) return;
    endingRef.current = true;
    setBusy(true);
    const endedAt = new Date().toISOString();
    const durationSeconds = current.connectedAt
      ? Math.round((Date.parse(endedAt) - Date.parse(current.connectedAt)) / 1000)
      : 0;
    callSession.update({ phase: "ended", endedAt, durationSeconds });
    call(ltrService.updateCall, current.caseId, current.callId, {
      ended: true,
      durationSeconds,
    })
      .then((updated) => callSession.syncCase(updated))
      .catch((err) => {
        console.error("Error ending LTR call:", err);
        EventService.emit("showError", {
          message: `The call ended but could not be saved on the case: ${
            err?.message || "please retry from the call audit"
          }`,
        });
      })
      .finally(() => {
        endingRef.current = false;
        setBusy(false);
      });
    if (fromStream) {
      EventService.emit("showError", { message: "Call ended" });
    }
  }

  const openStep = (kind) => {
    callSession.requestAction(kind);
    const target = `/miscellaneous/ltr/details/${session.caseId}`;
    const path = (history && history.location && history.location.pathname) || "";
    if (history && path !== target) history.push(target);
  };

  const stepTooltip = (kind) => {
    if (kind === "appointment" && appt) return `Appointment ${appt.appointmentId}`;
    if (kind === "rx" && appt && appt.prescriptionId) return `Rx ${appt.prescriptionId}`;
    if (kind === "action" && done.action) return "Action set";
    return STEPS.find((s) => s.kind === kind).label;
  };

  if (ended) {
    // SparkTG's record, once it is in, says how long the call really ran and
    // who missed it; until then the pill's own timing stands.
    const record = sessionCall(session);
    const telephony = record && record.telephony && record.telephony.disposition ? record.telephony : null;
    const talk = telephony ? telephony.talkSeconds || 0 : session.durationSeconds || 0;
    const missed = !!telephony && telephony.disposition === "missed";
    let receiptSub = clock(talk);
    if (missed && telephony.missedBy === "caller") receiptSub = "You didn't pick up";
    else if (missed && telephony.missedBy === "patient") receiptSub = "Patient didn't answer";
    else if (missed) receiptSub = "Not answered";
    return (
      <div
        className="ltr-call-pill ended"
        onMouseEnter={() => (hoverRef.current = true)}
        onMouseLeave={() => (hoverRef.current = false)}
        role="status"
      >
        <span className="ltr-call-pill-phone">
          <FontIcon>call</FontIcon>
        </span>
        <span className="ltr-call-pill-who">
          <span className="ltr-call-pill-name">{missed ? "Missed call" : "Call ended"}</span>
          <span className="ltr-call-pill-sub">{receiptSub}</span>
        </span>
        <span className="ltr-call-pill-divider" />
        <button
          type="button"
          className="ltr-call-pill-dismiss"
          onClick={() => callSession.clear()}
        >
          <FontIcon>close</FontIcon>
          Dismiss
        </button>
      </div>
    );
  }

  return (
    <div className="ltr-call-pill" role="status" aria-live="polite">
      <span className="ltr-call-pill-phone">
        <FontIcon>call</FontIcon>
      </span>
      <Tooltip
        arrow
        placement="top"
        title={connected ? `${session.ltrId || ""} · open case` : ringingTip}
      >
        <button
          type="button"
          className={`ltr-call-pill-who${connected || canTap ? "" : " waiting"}`}
          onClick={connected ? () => openStep("view") : markConnected}
          aria-disabled={!connected && !canTap}
        >
          <span className="ltr-call-pill-name">
            <span className={`ltr-call-pill-dot ${connected ? "live" : "ringing"}`} />
            {session.patientName || `PID-${session.patientId}`}
          </span>
          <span className="ltr-call-pill-sub">
            {connected ? clock(elapsed) : ringingLabel}
          </span>
        </button>
      </Tooltip>

      <span className="ltr-call-pill-divider" />

      {STEPS.map((step) => {
        if (done[step.kind]) {
          return (
            <Tooltip key={step.kind} arrow placement="top" title={stepTooltip(step.kind)}>
              <span className="ltr-call-pill-step done">
                <FontIcon>check</FontIcon>
              </span>
            </Tooltip>
          );
        }
        if (available(step.kind)) {
          return (
            <button
              key={step.kind}
              type="button"
              className="ltr-call-pill-cta"
              onClick={() => openStep(step.kind)}
            >
              <FontIcon>{step.icon}</FontIcon>
              {step.label}
            </button>
          );
        }
        return (
          <Tooltip
            key={step.kind}
            arrow
            placement="top"
            title={connected ? `${step.label} — after the previous step` : "Available once the call connects"}
          >
            <span className="ltr-call-pill-step locked">
              <FontIcon>{step.icon}</FontIcon>
            </span>
          </Tooltip>
        );
      })}

      <span className="ltr-call-pill-divider" />

      <Tooltip arrow placement="top" title="Hang up">
        <button
          type="button"
          className="ltr-call-pill-hangup"
          onClick={pressHangUp}
          disabled={busy}
          aria-label="Hang up"
        >
          <FontIcon>call_end</FontIcon>
        </button>
      </Tooltip>

      {confirmEnd && (
        <div className="ltr-call-pill-confirm" role="alertdialog" aria-label="The call is still on">
          <p>The call is still on. Hang up on your phone; the floater closes by itself when the call ends.</p>
          <div className="ltr-call-pill-confirm-actions">
            <button
              type="button"
              className="ltr-call-pill-confirm-secondary"
              onClick={() => {
                setConfirmEnd(false);
                hangUp();
              }}
            >
              Close anyway
            </button>
            <button type="button" className="ltr-call-pill-confirm-primary" onClick={() => setConfirmEnd(false)}>
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default InCallPill;
